/**
 * useSmartChoice - Main Hook
 *
 * SINGLE SOURCE OF TRUTH for SmartChoice eligibility
 *
 * Returns a complete eligibility object with all SmartChoice states.
 * Use the appropriate property based on your needs:
 * - isEnabled: Full eligibility check (category + context + flags)
 * - isAvailable: Feature flags check only (ignores category/context)
 *
 * @module useSmartChoice
 *
 * @example
 * // Default usage - respects category context
 * const { isEnabled, isAvailable } = useSmartChoice();
 * if (isEnabled) {
 *   // Show SmartChoice UI for current category
 * }
 *
 * @example
 * // "All" category - check if feature exists
 * const { isAvailable } = useSmartChoice();
 * if (isAvailable) {
 *   // Show SmartChoice is available
 * }
 *
 */

import { useMemo } from 'react';

import { CareCategories } from '../../common/Constants';
import { useSmartChoiceContext, useSmartChoiceFlags } from './hooks';
import { SmartChoiceEligibility } from './smartChoice.config';
import {
  determineSmartChoiceEligibility,
  getEligibleCategories,
  isNonSmartChoiceCategory,
  validateCoverageTypeForCategory,
} from './utils';

// ============================================================================
// MAIN HOOK
// ============================================================================

/**
 * useSmartChoice - Returns complete SmartChoice eligibility information
 *
 * @param options - Optional configuration
 * @param options.coverageType - Coverage type override for widget mode
 * @returns SmartChoiceEligibility - Complete eligibility object
 */
export function useSmartChoice(options?: {
  coverageType?: string;
}): SmartChoiceEligibility {
  const coverageType = options?.coverageType;
  const context = useSmartChoiceContext(coverageType);
  const flags = useSmartChoiceFlags();

  // Early return for Nice members
  if (context.isNiceMember) {
    return useMemo(
      () => ({
        isEnabled: false,
        isAvailable: false,
        currentCategory: context.category || context.chipValue,
        isNonSmartChoiceCategory: isNonSmartChoiceCategory(
          context.category || context.chipValue
        ),
        isEligibleCategory: false,
        eligibleCategories: [],
        flags: { ...flags, isSmartChoiceSuppressed: true },
        coverageType,
        coverageAllowsSmartChoice: false,
      }),
      [context.category, context.chipValue, flags, coverageType]
    );
  }

  // Compute full eligibility (with category/context validation)
  const fullResult = useMemo(
    () => determineSmartChoiceEligibility(context, flags, false),
    [context, flags]
  );

  // Compute flags-only eligibility (for "All" category)
  const flagsOnlyResult = useMemo(
    () => determineSmartChoiceEligibility(context, flags, true),
    [context, flags]
  );

  // Build complete eligibility object
  return useMemo(() => {
    const category = context.category || context.chipValue;
    const eligibleCategories = getEligibleCategories(flags);

    // Use category-specific coverage validation
    const coverageAllows = validateCoverageTypeForCategory(
      coverageType,
      category
    );

    const result: SmartChoiceEligibility = {
      // Primary states
      isEnabled: fullResult.isEnabled, // Full validation (category + context)
      isAvailable: flagsOnlyResult.isEnabled, // Flags only (for "All" chip)

      // Context data
      currentCategory: category,
      isNonSmartChoiceCategory: isNonSmartChoiceCategory(category),
      isEligibleCategory: eligibleCategories.includes(
        category as CareCategories
      ),
      eligibleCategories,

      // Configuration
      flags,

      // Coverage (for widget mode)
      coverageType,
      coverageAllowsSmartChoice: coverageAllows,
    };

    return result;
  }, [
    fullResult.isEnabled,
    flagsOnlyResult.isEnabled,
    context.category,
    context.chipValue,
    flags.enablePSXProviderRecommendations,
    flags.enablePSXFacilitiesRecommendations,
    flags.isSmartChoiceSuppressed,
    coverageType,
  ]);
}
